import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { api } from "../utils/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAdminEmail, setPendingAdminEmail] = useState(null);

  // On mount: check for an existing admin session via the cookie.
  useEffect(() => {
    api
      .get("/admin/me")
      .then((data) => setAdmin(data.admin || null))
      .catch(() => setAdmin(null))
      .finally(() => setLoading(false));
  }, []);

  // NOTE: there is no admin signIn() method. Sign-in goes through the
  // shared /api/auth/signin endpoint which forks server-side.
  // SignIn.js calls setPendingAdminEmail directly when the response
  // has route === "admin".

  const verify2FA = useCallback(async (code) => {
    if (!pendingAdminEmail) {
      return { ok: false, error: "No pending verification" };
    }
    try {
      const data = await api.post("/admin/verify-2fa", {
        email: pendingAdminEmail,
        code,
      });
      setAdmin(data.admin || null);
      setPendingAdminEmail(null);
      return { ok: true, mustChangePassword: !!data.mustChangePassword };
    } catch (err) {
      return { ok: false, error: err.message || "Invalid code" };
    }
  }, [pendingAdminEmail]);

  const resend2FA = useCallback(async () => {
    if (!pendingAdminEmail) {
      return { ok: false, error: "No pending verification" };
    }
    try {
      await api.post("/admin/resend-2fa", { email: pendingAdminEmail });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Failed to resend" };
    }
  }, [pendingAdminEmail]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/admin/signout");
    } catch {
      // swallow — we still clear local state
    }
    setAdmin(null);
    setPendingAdminEmail(null);
  }, []);

  const impersonate = useCallback(async (uid) => {
    const data = await api.post(`/admin/users/${uid}/impersonate`);
    return data; // { ok, targetUser }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post("/admin/change-password", { currentPassword, newPassword });
    // Server updates passwordChangedAt; refetch /admin/me to pick it up.
    try {
      const data = await api.get("/admin/me");
      setAdmin(data.admin || null);
    } catch {
      // swallow
    }
  }, []);

  const value = {
    admin,
    loading,
    pendingAdminEmail,
    setPendingAdminEmail,
    verify2FA,
    resend2FA,
    signOut,
    impersonate,
    changePassword,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
